import { useEffect, useMemo, useRef, useState } from 'react'
import { ZoomIn } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { loadImage, DEFAULT_CROP } from '@/lib/imageUtils'

const FRAME = 240
const MAX_ZOOM = 4

// Square logo cropper: drag the image to position it and use the slider to zoom.
// Output is a (zoom, center) crop – stored alongside the source so it can be
// reopened and re-adjusted later.
export default function LogoCropper({ open, source, initialCrop, onSave, onCancel }) {
  const [crop, setCrop] = useState(initialCrop || DEFAULT_CROP)
  const [dims, setDims] = useState(null)
  const dragRef = useRef(null)

  useEffect(() => { setCrop(initialCrop || DEFAULT_CROP) }, [initialCrop, source])
  useEffect(() => {
    let alive = true
    if (source) loadImage(source).then((img) => { if (alive) setDims({ w: img.naturalWidth, h: img.naturalHeight }) }).catch(() => {})
    return () => { alive = false }
  }, [source])

  // Keep the crop center inside the image for a given zoom (so the square never
  // shows empty edges).
  const clampCenter = (cx, cy, zoom, d = dims) => {
    if (!d) return { cx, cy }
    const size = Math.min(d.w, d.h) / Math.max(1, zoom)
    const hx = (size / 2) / d.w
    const hy = (size / 2) / d.h
    return {
      cx: Math.min(1 - hx, Math.max(hx, cx)),
      cy: Math.min(1 - hy, Math.max(hy, cy)),
    }
  }

  const transform = useMemo(() => {
    if (!dims) return null
    const minSide = Math.min(dims.w, dims.h)
    const scale = (FRAME * Math.max(1, crop.zoom)) / minSide
    return {
      scale,
      dispW: dims.w * scale,
      dispH: dims.h * scale,
      ox: FRAME / 2 - crop.cx * dims.w * scale,
      oy: FRAME / 2 - crop.cy * dims.h * scale,
    }
  }, [dims, crop])

  const onPointerDown = (event) => {
    if (!transform) return
    dragRef.current = { x: event.clientX, y: event.clientY, cx: crop.cx, cy: crop.cy }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const onPointerMove = (event) => {
    if (!dragRef.current || !transform || !dims) return
    const dx = event.clientX - dragRef.current.x
    const dy = event.clientY - dragRef.current.y
    const ncx = dragRef.current.cx - dx / (dims.w * transform.scale)
    const ncy = dragRef.current.cy - dy / (dims.h * transform.scale)
    setCrop((c) => ({ ...c, ...clampCenter(ncx, ncy, c.zoom) }))
  }
  const onPointerUp = () => { dragRef.current = null }

  const onZoom = (event) => {
    const zoom = Number(event.target.value)
    setCrop((c) => ({ ...c, zoom, ...clampCenter(c.cx, c.cy, zoom) }))
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Position your logo</DialogTitle>
          <DialogDescription>Drag to reposition, slide to zoom. This crops to a square.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3">
          <div
            className="relative overflow-hidden rounded-lg border border-border bg-[repeating-conic-gradient(#f3f3f3_0_25%,#fff_0_50%)] bg-[length:16px_16px] touch-none"
            style={{ width: FRAME, height: FRAME, cursor: dragRef.current ? 'grabbing' : 'grab' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {source && transform && (
              <img
                src={source}
                alt="Logo to crop"
                draggable={false}
                className="absolute max-w-none select-none"
                style={{ left: transform.ox, top: transform.oy, width: transform.dispW, height: transform.dispH }}
              />
            )}
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10" />
          </div>

          <div className="flex w-full items-center gap-2">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.02}
              value={crop.zoom}
              onChange={onZoom}
              aria-label="Zoom"
              className="w-full accent-indigo-600"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="button" onClick={() => onSave(crop)}>Use logo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
